import { FC, useCallback } from 'react';
import { useTranslation } from 'next-i18next';
import Label from 'src/common/components/Label';
import Typography from 'src/common/components/Typography';
import {
  FIRST_NAME, EMAIL, PHONE, type BookNowDirectFormState,
} from 'src/book-now-direct/constants';
import TextInput from 'src/common/components/Input/TextInput';
import EmailInput from 'src/common/components/Input/EmailInput';
import PhoneNumberInput from 'src/common/components/Input/PhoneNumberInput';
import InputWrapper from 'src/common/components/InputWrapper';
import GrecaptchaCompliance from 'src/common/components/GrecaptchaCompliance';
import { useBookNowDirectContext } from 'src/book-now-direct/providers/BookNowDirectProvider';
import BackButton from 'src/admin/listing-editor/components/BackButton';
import { useFormState } from 'react-final-form';
import styles from './FormStepGuest.module.scss';

/**
 * Optional step shown only if `!user`
 * If user has an account already, the 'SignIn' link redirects to login
 * If submitted email is already registered, the PATCH will fail and redirect to login
 * Else, the PATCH will create a guest account and redirrect to stripe
 * */
const FormStepGuest: FC = () => {
  const { t } = useTranslation('book-now-direct');
  const {
    signInThenCheckout, formStep, changeStep,
  } = useBookNowDirectContext();
  const { values } = useFormState<BookNowDirectFormState>();

  const signInLink = useCallback(() => {
    signInThenCheckout(values);
  }, [signInThenCheckout, values]);

  if (formStep !== 'guest') {
    return null;
  }

  return (
    <div className={styles.root}>
      <div className={styles.title}>
        <div className={styles.back}>
          <BackButton type="button" onClick={() => changeStep('details')} />
        </div>
        <Typography type="h1" size="heading-3">
          {t('guest-title')}
        </Typography>
      </div>
      <div className={styles.description}>
        <Typography type="p" size="body-m">
          {t('guest-description')}
        </Typography>
      </div>
      <div className={styles.form}>
        <fieldset className={styles.fieldset}>
          <Label
            required
            htmlFor={FIRST_NAME}
            label={t('guest-first-name-label')}
            size="m"
          />
          <InputWrapper
            name={FIRST_NAME}
            showErrorMessageIcon
            showErrorOnTouch
            preventErrorMessageLayoutShift={false}
          >
            <TextInput
              name={FIRST_NAME}
              allowAutoComplete={false}
            />
          </InputWrapper>
        </fieldset>
        <fieldset className={styles.fieldset}>
          <Label
            required
            htmlFor={EMAIL}
            label={t('guest-email-label')}
            size="m"
          />
          <InputWrapper
            name={EMAIL}
            showErrorMessageIcon
            showErrorOnTouch
            classNameModifier="email"
            preventErrorMessageLayoutShift={false}
          >
            <EmailInput
              name={EMAIL}
              allowAutoComplete={false}
            />
          </InputWrapper>
        </fieldset>
        <fieldset className={styles.fieldset}>
          <Label
            htmlFor={PHONE}
            label={t('guest-phone-label')}
            size="m"
          />
          <InputWrapper
            name={PHONE}
            showErrorOnTouch
            showErrorMessageIcon
            preventErrorMessageLayoutShift={false}
          >
            <PhoneNumberInput name={PHONE} />
          </InputWrapper>
        </fieldset>
      </div>
      <div className={styles.signInRow}>
        <Typography type="p" size="body-m">
          {t('guest-sign-in-question')}
        </Typography>
        <button
          type="button"
          onClick={signInLink}
          className={styles.signIn}
        >
          <Typography type="p" size="body-m">
            {t('guest-sign-in-link')}
          </Typography>
        </button>
      </div>
      <GrecaptchaCompliance />
    </div>
  );
};

export default FormStepGuest;
